import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  onSnapshot,
  Timestamp,
  updateDoc,
  setDoc,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from './firebase';

export type SubscriptionTier = 'free' | 'pro' | 'enterprise';
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
export type BillingInterval = 'month' | 'year';

export interface SubscriptionLimits {
  maxTeams: number;
  maxProjects: number;
  maxAISummariesPerMonth: number;
  maxSlackChannels: number;
  hasStandups: boolean;
  hasRollups: boolean;
  hasAdvancedSearch: boolean;
  hasPrioritySupport: boolean;
  hasSlackIntegration: boolean;
  hasAssistant: boolean;
}

// AI features that count toward the monthly limit
// All AI API calls are tracked and count toward limits
export const BILLABLE_AI_FEATURES = [
  'feed-summary',
  'rollup-summary',
  'reflection-summary',
  'standup-summary',
  'retro-synthesis',
  'weekly-summary',
  'standup-submission-summary',
  'update-title'
] as const;
export type BillableAIFeature = typeof BILLABLE_AI_FEATURES[number];

export interface OrgSubscription {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodEnd?: Timestamp;
  cancelAtPeriodEnd?: boolean;
  billingInterval?: BillingInterval;
  seatCount?: number; // Purchased seat count
}

export interface SeatSnapshot {
  count: number;
  timestamp: Timestamp;
  charged: boolean;
  overageCount?: number;
  nextBillingDate?: Timestamp;
}

export interface SeatUsageInfo {
  currentSeats: number;
  purchasedSeats: number;
  overageSeats: number;
  isOverLimit: boolean;
  nextTrueUpDate: Date | null;
  gracePeriodRemaining: number; // How many more seats before billing
}

// Define limits for each tier
export const TIER_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  free: {
    maxTeams: 2,
    maxProjects: 20,
    maxAISummariesPerMonth: 100, // Generous free tier for AI features
    maxSlackChannels: 1,
    hasStandups: true,
    hasRollups: true,
    hasAdvancedSearch: false,
    hasPrioritySupport: false,
    hasSlackIntegration: true, // Now allowed with limits
    hasAssistant: false,
  },
  pro: {
    maxTeams: Infinity,
    maxProjects: Infinity,
    maxAISummariesPerMonth: Infinity,
    maxSlackChannels: Infinity,
    hasStandups: true,
    hasRollups: true,
    hasAdvancedSearch: true,
    hasPrioritySupport: true,
    hasSlackIntegration: true,
    hasAssistant: true,
  },
  enterprise: {
    maxTeams: Infinity,
    maxProjects: Infinity,
    maxAISummariesPerMonth: Infinity,
    maxSlackChannels: Infinity,
    hasStandups: true,
    hasRollups: true,
    hasAdvancedSearch: true,
    hasPrioritySupport: true,
    hasSlackIntegration: true,
    hasAssistant: true,
  },
};

/**
 * Get organization's subscription status
 */
export async function getOrgSubscription(orgId: string): Promise<OrgSubscription> {
  const orgDoc = await getDoc(doc(db, 'orgs', orgId));

  if (!orgDoc.exists()) {
    throw new Error('Organization not found');
  }

  const orgData = orgDoc.data();

  return {
    tier: orgData.subscriptionTier || 'free',
    status: orgData.subscriptionStatus || 'active',
    stripeCustomerId: orgData.stripeCustomerId,
    stripeSubscriptionId: orgData.stripeSubscriptionId,
    currentPeriodEnd: orgData.currentPeriodEnd,
    cancelAtPeriodEnd: orgData.cancelAtPeriodEnd,
    seatCount: orgData.seatCount,
    billingInterval: orgData.billingInterval,
  };
}

/**
 * Get limits for an organization based on their subscription
 */
export async function getOrgLimits(orgId: string): Promise<SubscriptionLimits> {
  const subscription = await getOrgSubscription(orgId);
  return TIER_LIMITS[subscription.tier];
}

/**
 * Check if organization can create a new team
 */
export async function canCreateTeam(orgId: string): Promise<{ allowed: boolean; reason?: string; currentCount?: number; limit?: number }> {
  const limits = await getOrgLimits(orgId);

  if (limits.maxTeams === Infinity) {
    return { allowed: true };
  }

  const teamsRef = collection(db, 'orgs', orgId, 'teams');
  const teamsSnapshot = await getDocs(teamsRef);

  // Only count teams that haven't been soft-deleted
  const currentCount = teamsSnapshot.docs.filter(doc => {
    const data = doc.data();
    return !data.deletedAt;
  }).length;

  if (currentCount >= limits.maxTeams) {
    return {
      allowed: false,
      reason: `You've reached the maximum of ${limits.maxTeams} teams on the Free plan`,
      currentCount,
      limit: limits.maxTeams
    };
  }

  return { allowed: true, currentCount, limit: limits.maxTeams };
}

/**
 * Check if organization can create a new project
 */
export async function canCreateProject(orgId: string): Promise<{ allowed: boolean; reason?: string; currentCount?: number; limit?: number }> {
  const limits = await getOrgLimits(orgId);

  if (limits.maxProjects === Infinity) {
    return { allowed: true };
  }

  const projectsRef = collection(db, 'orgs', orgId, 'projects');
  const projectsSnapshot = await getDocs(projectsRef);

  // Only count projects that haven't been soft-deleted
  const currentCount = projectsSnapshot.docs.filter(doc => {
    const data = doc.data();
    return !data.deletedAt;
  }).length;

  if (currentCount >= limits.maxProjects) {
    return {
      allowed: false,
      reason: `You've reached the maximum of ${limits.maxProjects} projects on the Free plan`,
      currentCount,
      limit: limits.maxProjects
    };
  }

  return { allowed: true, currentCount, limit: limits.maxProjects };
}

/**
 * Create a Stripe Checkout session for upgrading
 * This writes to Firestore and the Stripe extension handles the rest
 */
export async function createCheckoutSession(
  userId: string,
  orgId: string,
  priceId: string,
  successUrl: string,
  cancelUrl: string,
  quantity: number = 1
): Promise<string> {
  const checkoutSessionRef = collection(
    db,
    'customers',
    userId,
    'checkout_sessions'
  );

  try {
    const sessionData: any = {
      price: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
      quantity: quantity, // Always pass quantity for per-seat pricing
      allow_promotion_codes: true, // Enable coupon/promo codes at checkout
      metadata: {
        orgId,
      },
      // Pass metadata through to the Stripe subscription itself
      // so webhooks can map subscription events back to the org
      subscription_data: {
        metadata: {
          orgId,
        },
      },
    };

    const session = await addDoc(checkoutSessionRef, sessionData);



    // Wait for the extension to create the session
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.error('Timeout: Stripe extension did not respond within 10 seconds');
        unsubscribe();
        reject(new Error('Timeout waiting for checkout session. Is the Stripe extension installed and configured?'));
      }, 10000);

      const unsubscribe = onSnapshot(session, (snap) => {
        const data = snap.data();

        if (data?.error) {
          console.error('Stripe extension returned error:', data.error);
          clearTimeout(timeout);
          unsubscribe();
          reject(new Error(data.error.message));
        }
        if (data?.url) {
          clearTimeout(timeout);
          unsubscribe();
          resolve(data.url);
        }
      }, (error) => {
        console.error('Error listening to Stripe checkout session:', error);
        clearTimeout(timeout);
        unsubscribe();
        reject(new Error(`Checkout session listener failed: ${error.message}`));
      });
    });
  } catch (error: any) {
    console.error('Error creating checkout session document:', error);
    throw new Error(`Failed to create checkout session: ${error.message}`);
  }
}

/**
 * Create a link to the Stripe Customer Portal
 * This allows users to manage their subscription, update payment method, etc.
 */
export async function createPortalLink(
  userId: string,
  returnUrl: string
): Promise<string> {
  // Use the Firebase callable function provided by the Stripe Extension
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('./firebase');
  const createPortalLinkFn = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');

  try {
    const result = await createPortalLinkFn({ returnUrl });
    const data = result.data as { url: string };
    return data.url;
  } catch (error: any) {
    console.error('Error creating portal link:', error);
    throw new Error(`Failed to create portal link: ${error.message}`);
  }
}

/**
 * Listen to subscription changes for a user
 * Returns an unsubscribe function
 */
export function subscribeToUserSubscriptions(
  userId: string,
  callback: (subscriptions: any[]) => void
) {
  const subscriptionsRef = collection(db, 'customers', userId, 'subscriptions');
  const q = query(subscriptionsRef, where('status', 'in', ['trialing', 'active']));

  return onSnapshot(q, (snapshot) => {
    const subscriptions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(subscriptions);
  }, (error) => {
    console.error('Error listening to user subscriptions:', error);
    callback([]);
  });
}

/**
 * Update organization subscription tier based on Stripe subscription
 * This should be called from a Cloud Function when subscription status changes
 */
export async function updateOrgSubscriptionTier(
  orgId: string,
  tier: SubscriptionTier,
  status: SubscriptionStatus,
  stripeSubscriptionId?: string,
  currentPeriodEnd?: Timestamp
) {
  const orgRef = doc(db, 'orgs', orgId);

  await updateDoc(orgRef, {
    subscriptionTier: tier,
    subscriptionStatus: status,
    stripeSubscriptionId,
    currentPeriodEnd,
  });
}

// ============================================
// SEAT MANAGEMENT & TRUE-UP FUNCTIONS
// ============================================

const SEAT_GRACE_LIMIT = 5; // Allow +5 seats before billing

/**
 * Get current seat count for an organization
 * Only counts non-viewer members (viewers are free)
 */
export async function getCurrentSeatCount(orgId: string): Promise<number> {
  const membershipsRef = collection(db, 'orgs', orgId, 'memberships');
  const snapshot = await getDocs(membershipsRef);

  // Count only members who aren't viewers (members, managers, admins, owners are billable)
  let billableSeats = 0;
  snapshot.forEach((doc) => {
    const data = doc.data();
    const role = data.role as string;

    // Exclude viewers from seat count
    if (role !== 'viewer') {
      billableSeats++;
    }
  });

  return billableSeats;
}

/**
 * Calculate next true-up date based on billing interval
 */
export function calculateNextTrueUpDate(
  currentPeriodEnd: Timestamp,
  billingInterval: BillingInterval
): Date {
  const periodEnd = currentPeriodEnd.toDate();

  if (billingInterval === 'month') {
    // Monthly plans: true-up on 1st of next month
    const nextMonth = new Date(periodEnd);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1);
    return nextMonth;
  } else {
    // Annual plans: true-up quarterly
    const now = new Date();
    const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const nextQuarter = new Date(quarterStart);
    nextQuarter.setMonth(quarterStart.getMonth() + 3);
    return nextQuarter;
  }
}

/**
 * Get comprehensive seat usage information for an organization
 */
export async function getSeatUsageInfo(orgId: string): Promise<SeatUsageInfo> {
  const subscription = await getOrgSubscription(orgId);
  const currentSeats = await getCurrentSeatCount(orgId);
  const purchasedSeats = subscription.seatCount || 1;
  const overageSeats = Math.max(0, currentSeats - purchasedSeats);
  const isOverLimit = overageSeats > SEAT_GRACE_LIMIT;

  let nextTrueUpDate: Date | null = null;
  if (subscription.currentPeriodEnd && subscription.billingInterval) {
    nextTrueUpDate = calculateNextTrueUpDate(
      subscription.currentPeriodEnd,
      subscription.billingInterval
    );
  }

  const gracePeriodRemaining = Math.max(0, SEAT_GRACE_LIMIT - overageSeats);

  return {
    currentSeats,
    purchasedSeats,
    overageSeats,
    isOverLimit,
    nextTrueUpDate,
    gracePeriodRemaining
  };
}

/**
 * Create a seat snapshot for billing reconciliation
 * Called by Cloud Function during true-up periods
 */
export async function createSeatSnapshot(
  orgId: string,
  count: number,
  charged: boolean,
  overageCount?: number,
  nextBillingDate?: Timestamp
): Promise<void> {
  const now = new Date();
  const snapshotId = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const snapshotRef = doc(db, 'orgs', orgId, 'seatSnapshots', snapshotId);

  await setDoc(snapshotRef, {
    count,
    timestamp: Timestamp.now(),
    charged,
    overageCount,
    nextBillingDate
  });
}

/**
 * Get the most recent seat snapshot
 */
export async function getLatestSeatSnapshot(orgId: string): Promise<SeatSnapshot | null> {
  const snapshotsRef = collection(db, 'orgs', orgId, 'seatSnapshots');
  const q = query(snapshotsRef, orderBy('timestamp', 'desc'), limit(1));
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0].data() as SeatSnapshot;
}

// ============================================
// AI USAGE LIMITS
// ============================================

/**
 * Get the count of billable AI summaries used this month for an org
 */
export async function getOrgMonthlyAISummaryCount(orgId: string): Promise<number> {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');

  // Query all daily usage docs for the current month
  const usageRef = collection(db, 'orgs', orgId, 'ai_usage_daily');
  const startDate = `${year}-${month}-01`;
  const endDate = `${year}-${month}-31`;

  const q = query(
    usageRef,
    where('date', '>=', startDate),
    where('date', '<=', endDate)
  );

  const snapshot = await getDocs(q);

  let totalCount = 0;
  snapshot.forEach((doc) => {
    const data = doc.data();
    // Sum up counts for billable features only
    BILLABLE_AI_FEATURES.forEach((feature) => {
      totalCount += data.byFeature?.[feature]?.count || 0;
    });
  });

  return totalCount;
}

/**
 * Check if organization can use AI summary features
 */
export async function canUseAISummary(orgId: string): Promise<{
  allowed: boolean;
  reason?: string;
  currentCount?: number;
  limit?: number;
  resetDate?: Date;
}> {
  try {
    const subscription = await getOrgSubscription(orgId);
    const limits = TIER_LIMITS[subscription.tier];

    if (limits.maxAISummariesPerMonth === Infinity) {
      return { allowed: true };
    }

    const currentCount = await getOrgMonthlyAISummaryCount(orgId);

    // Calculate reset date (first of next month)
    const now = new Date();
    const resetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    if (currentCount >= limits.maxAISummariesPerMonth) {
      return {
        allowed: false,
        reason: `You've used all ${limits.maxAISummariesPerMonth} summaries this month. Resets ${resetDate.toLocaleDateString()}.`,
        currentCount,
        limit: limits.maxAISummariesPerMonth,
        resetDate,
      };
    }

    return {
      allowed: true,
      currentCount,
      limit: limits.maxAISummariesPerMonth,
      resetDate,
    };
  } catch (error) {
    // This check reads via the CLIENT Firebase SDK. Called from an /api/ai
    // route (server), the client SDK is unauthenticated and Firestore rules
    // deny the read → it throws `permission-denied`, which every AI route was
    // surfacing as a 502 "AI generation failed". Fail OPEN: a broken/denied
    // usage read must never block generation. The per-user, admin-SDK
    // enforceAiDailyLimit (lib/server/ai-rate-limit) is the real server-side
    // guard; org monthly caps still apply to authenticated client-side callers.
    // (Proper server-side org-cap enforcement is a follow-up.)
    console.warn(
      '[canUseAISummary] usage check failed; allowing generation:',
      (error as Error)?.message
    );
    return { allowed: true };
  }
}

/**
 * Increment the monthly AI summary count for an org.
 *
 * Note: This is now handled by trackAIUsage() in lib/server/ai-usage-tracking.ts,
 * which updates the byFeature counts atomically. This function is kept for
 * backwards compatibility but is essentially a no-op.
 *
 * @deprecated Use trackAIUsage() instead which handles incrementing automatically
 */
export async function incrementOrgMonthlyAISummaryCount(_orgId: string): Promise<void> {
  // No-op: counting is handled by trackAIUsage() in ai-usage-tracking.ts
  // which updates orgs/{orgId}/ai_usage_daily/{date}.byFeature.{feature}.count
}

// ============================================
// SLACK CHANNEL LIMITS
// ============================================

/**
 * Count unique Slack channels configured for an org
 */
export async function getSlackChannelCount(orgId: string): Promise<number> {
  const slackDoc = await getDoc(doc(db, 'orgs', orgId, 'slack', 'connection'));

  if (!slackDoc.exists()) {
    return 0;
  }

  const channels = slackDoc.data()?.channels || {};
  // Count unique channel IDs (same channel can be used for multiple purposes)
  const uniqueChannelIds = new Set(
    Object.values(channels).filter((id): id is string => Boolean(id))
  );

  return uniqueChannelIds.size;
}

/**
 * Check if organization can add another Slack channel
 */
export async function canAddSlackChannel(orgId: string, newChannelId?: string): Promise<{
  allowed: boolean;
  reason?: string;
  currentCount?: number;
  limit?: number;
}> {
  const subscription = await getOrgSubscription(orgId);
  const limits = TIER_LIMITS[subscription.tier];

  if (limits.maxSlackChannels === Infinity) {
    return { allowed: true };
  }

  const slackDoc = await getDoc(doc(db, 'orgs', orgId, 'slack', 'connection'));

  if (!slackDoc.exists()) {
    return { allowed: true, currentCount: 0, limit: limits.maxSlackChannels };
  }

  const channels = slackDoc.data()?.channels || {};
  const uniqueChannelIds = new Set(
    Object.values(channels).filter((id): id is string => Boolean(id))
  );

  // If new channel is already in use, it doesn't count as adding a new one
  if (newChannelId && uniqueChannelIds.has(newChannelId)) {
    return { allowed: true, currentCount: uniqueChannelIds.size, limit: limits.maxSlackChannels };
  }

  const currentCount = uniqueChannelIds.size;

  if (currentCount >= limits.maxSlackChannels) {
    return {
      allowed: false,
      reason: `Free plan allows ${limits.maxSlackChannels} Slack channel. Upgrade to Pro for unlimited channels.`,
      currentCount,
      limit: limits.maxSlackChannels,
    };
  }

  return { allowed: true, currentCount, limit: limits.maxSlackChannels };
}
